import { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { get } from "lodash"
import { Creators as IntradayCreators } from "model/intraday"
import { Creators as DataCreators } from "model/data"
import { generateIntradayBatchUrlAt, getIntradayBatchQueryAsOf } from "helpers/intraday"
import { useAccountAlias, useAccountId } from "resources/account"
import { DataStatus } from "helpers/data-statuses"
import { Uuid } from "helpers/types/uuid"
import { IsoDate, IsoTime } from "helpers/types/dates"
import { InstrumentWithMetadata } from "helpers/types/instruments/metadata"
import { ResourceType } from "helpers/permissions/resource-types"
import { IntradayBatchMetadata, IntradayQueryKey, IntradayQueryRecord, ReduxState } from "model/state"
import useResource from "./resources"

const INTRADAY_RESOURCE = "intradayLastBatch"
const EMPTY_OBJECT: Record<IntradayQueryKey, IntradayQueryRecord> = {}

// Single source for the resource key so the channel's socket registration and the hook's read
// can't drift apart (a mismatch silently doubles the GET on every refresh tick).
export const intradayLastBatchResource = (id: string) => `${INTRADAY_RESOURCE}-${id}`

export function useIntradayChannel(useIntraday: boolean, resourceId: Uuid, resourceType: ResourceType, viewName: string) {
  const intradayQueries = useSelector((state: ReduxState) => get(state, "global.intraday.queries", EMPTY_OBJECT))
  const asOf = useSelector(getIntradayBatchQueryAsOf)
  const dispatch = useDispatch()
  const accountId = useAccountId()
  const accountAlias = useAccountAlias()

  useEffect(() => {
    if (!useIntraday || !accountId || !resourceId || resourceType !== "PORTFOLIO") {
      dispatch(IntradayCreators.unregisterModuleQueries(viewName))
      return
    }

    // id is the same as the url since we're not using graphql
    const id = generateIntradayBatchUrlAt(accountAlias, resourceId, asOf)
    const channel: IntradayQueryKey = `${accountId}_${resourceId}`
    const intradayResource = intradayLastBatchResource(resourceId)
    const currentId = get(intradayQueries, [channel, intradayResource, "id"], null)

    if (currentId !== id) {
      if (id) {
        dispatch(IntradayCreators.registerQuery(viewName, channel, intradayResource, { url: id, id }))
      } else {
        dispatch(IntradayCreators.unregisterQuery(viewName, channel, intradayResource))
      }
    }
  }, [viewName, dispatch, intradayQueries, useIntraday, accountId, resourceId, asOf, accountAlias, resourceType])

  // This needs to be a separate hook as we ONLY want to unregister if viewName changes
  // Unregistering otherwise will create an infinite loop.
  useEffect(() => {
    return () => {
      dispatch(IntradayCreators.unregisterModuleQueries(viewName))
    }
  }, [dispatch, viewName])
}

/**
 * Hook to get the last intraday batch data for a specific portfolio
 */
export function useLastIntradayBatch(instrument?: InstrumentWithMetadata, intradayEnabled?: boolean): [IntradayBatchMetadata | null, DataStatus] {
  const accountAlias = useAccountAlias()
  const asOf = useSelector(getIntradayBatchQueryAsOf)
  const { type, id } = instrument || {}

  return useResource({
    // asOf stays out of the key — it changes every refresh tick, so keying on it would leak a slot per tick.
    // Same abandoned-slot tradeoff as classificationsValues on portfolio switch — deliberate, payload is small.
    resource: id ? intradayLastBatchResource(id) : "intradayLastBatch",
    url: () => type === "PORTFOLIO" && intradayEnabled && id
      ? generateIntradayBatchUrlAt(accountAlias, id, asOf)
      : null,
  }, "data")
}

// Distinct from IntradayBatchMetadata (model/state.ts) — that's the single-latest-batch Redux
// shape useLastIntradayBatch surfaces; this is one item from the intradayBatches list query below.
export interface IntradayBatch {
  id: Uuid
  date: IsoDate
  asOf: IsoTime
  complete: boolean
}

// Every batch-trend graph template mounts its own useIntradayBatches for the same instrument,
// sharing one resource key — ref-count so unmounting one template doesn't reset the collection
// out from under sibling templates still reading it.
const intradayBatchesResourceRefCounts = new Map<string, number>()

/**
 * Hook to list today's committed intraday batches for a portfolio, used to build a batch-trend
 * time series (one point per completed batch) — distinct from useLastIntradayBatch, which only
 * surfaces the single latest batch.
 */
export function useIntradayBatches(instrument?: InstrumentWithMetadata, intradayEnabled?: boolean, date?: IsoDate, lastIntradayBatchId?: string | null): [IntradayBatch[] | null, DataStatus] {
  const dispatch = useDispatch()
  const { type, id } = instrument || {}
  const resource = id ? `intradayBatches-${id}` : "intradayBatches"

  // Per-instrument key, never reused — clear it on last unmount or it leaks a batch list per instrument.
  useEffect(() => {
    const count = (intradayBatchesResourceRefCounts.get(resource) ?? 0) + 1
    intradayBatchesResourceRefCounts.set(resource, count)

    return () => {
      const nextCount = (intradayBatchesResourceRefCounts.get(resource) ?? 1) - 1
      if (nextCount <= 0) {
        intradayBatchesResourceRefCounts.delete(resource)
        dispatch(DataCreators.resetCollections([resource]))
      } else {
        intradayBatchesResourceRefCounts.set(resource, nextCount)
      }
    }
  }, [dispatch, resource])

  return useResource({
    resource,
    url: () => type === "PORTFOLIO" && intradayEnabled && id && date
      ? {
        // Keyed on lastIntradayBatchId, not the live-tick asOf — batch membership only changes
        // when a batch commits, not on every socket pulse.
        id: `intradayBatches-${id}-${date}-${lastIntradayBatchId ?? "none"}`,
        query: `query ($date: Date, $resourceId: IdOrShortIdOrAlias, $resourceType: ResourceType) {
          intradayBatches(date: $date, resourceId: $resourceId, resourceType: $resourceType) {
            id
            date
            asOf
            complete
          }
        }`,
        variables: { date, resourceId: id, resourceType: type },
      }
      : null,
  }, "intradayBatches")
}
